import { useEffect, useRef, useState } from 'react';

interface HeroProps {
  onCTAClick: () => void;
}

const READY_URL = 'https://ready.intevolabs.com';

interface ChatMsg {
  side: 'in' | 'out';
  text: string;
  time: string;
}

const SCRIPT: ChatMsg[] = [
  { side: 'in', text: 'Hola, vi el apto en Costa del Este en Encuentra24, ¿sigue disponible?', time: '10:41' },
  { side: 'out', text: '¡Hola! Sí, el PH Marina Bay 12B sigue disponible 🏢 2 recámaras, 145m², balcón con vista al mar. $285,000. ¿Te cuento más detalles o prefieres agendar una visita?', time: '10:41' },
  { side: 'in', text: 'Cuéntame más', time: '10:42' },
  { side: 'out', text: '¿Lo buscas para vivir o como inversión?', time: '10:42' },
  { side: 'in', text: 'Para vivir', time: '10:43' },
  { side: 'out', text: '¿El precio está dentro de lo que manejas, o buscas algo en otro rango?', time: '10:43' },
  { side: 'in', text: 'Sí, está bien', time: '10:43' },
  { side: 'out', text: '¿Para cuándo te gustaría mudarte? Así te muestro las opciones de visita que más te convengan.', time: '10:44' },
  { side: 'in', text: 'En los próximos 2 meses', time: '10:44' },
  { side: 'out', text: 'Tengo disponibilidad este jueves 3:00pm o el sábado 10:00am para visitar el apartamento. ¿Cuál te queda mejor?', time: '10:45' },
  { side: 'in', text: 'El sábado a las 10', time: '10:45' },
  { side: 'out', text: 'Confirmado ✅ Visita: sábado 10:00am en PH Marina Bay, Costa del Este. Te llega un recordatorio 1 hora antes.', time: '10:45' },
];

function ChatBubble({ msg }: { msg: ChatMsg }) {
  return (
    <div className={`flex ${msg.side === 'in' ? 'justify-start' : 'justify-end'}`}>
      <div
        className="max-w-[82%] px-[11px] py-[8px] text-[12.5px] leading-[1.45] text-[#e9edef]"
        style={{
          borderRadius: '9px',
          borderTopLeftRadius: msg.side === 'in' ? '2px' : '9px',
          borderTopRightRadius: msg.side === 'out' ? '2px' : '9px',
          background: msg.side === 'in' ? '#202c33' : '#0a3d2e',
          animation: 'fireIn .45s ease forwards',
        }}
      >
        {msg.text}
        <span className="block text-[9.5px] text-[#7d8b93] mt-[3px] text-right">
          {msg.time}
          {msg.side === 'out' && <span className="text-[#53bdeb] ml-[3px]">✓✓</span>}
        </span>
      </div>
    </div>
  );
}

function TypingRow() {
  return (
    <div className="flex justify-start">
      <div
        className="flex gap-1 px-[13px] py-[10px]"
        style={{ background: '#202c33', borderRadius: '9px', borderTopLeftRadius: '2px', animation: 'fireIn .3s ease forwards' }}
      >
        {[0, 0.2, 0.4].map((delay, i) => (
          <span
            key={i}
            className="w-[5px] h-[5px] rounded-full"
            style={{ background: '#8696a0', animation: 'typingBlink 1.2s infinite', animationDelay: `${delay}s` }}
          />
        ))}
      </div>
    </div>
  );
}

function WhatsAppPhone() {
  const [shown, setShown] = useState<ChatMsg[]>([]);
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const playedRef = useRef(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const playScript = async () => {
      if (playedRef.current) return;
      playedRef.current = true;

      if (reduceMotion) {
        setShown(SCRIPT);
        return;
      }

      for (const msg of SCRIPT) {
        if (msg.side === 'out') {
          setTyping(true);
          const typeMs = 850 + Math.min(msg.text.length * 20, 2000);
          await wait(typeMs);
          setTyping(false);
        } else {
          await wait(1000);
        }
        setShown((prev) => [...prev, msg]);
        const readMs = 800 + Math.min(msg.text.length * 24, 2400);
        await wait(readMs);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playScript();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (stageRef.current) observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [shown, typing]);

  return (
    <div ref={stageRef} className="relative flex justify-center w-full">
      <div
        className="relative w-full"
        style={{
          maxWidth: '300px',
          aspectRatio: '300 / 610',
          background: '#050608',
          borderRadius: '42px',
          padding: '12px',
          boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)',
        }}
      >
        <div
          className="absolute z-10"
          style={{ top: '12px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '22px', background: '#050608', borderRadius: '0 0 16px 16px' }}
        />
        <div style={{ width: '100%', height: '100%', background: '#0b141a', borderRadius: '32px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div className="flex items-center gap-2.5 flex-shrink-0" style={{ background: '#1f2c34', padding: '26px 16px 12px' }}>
            <div
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-semibold"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #A78BFA)', fontFamily: 'IBM Plex Mono, monospace' }}
            >
              IL
            </div>
            <div>
              <div className="text-[14.5px] font-semibold text-[#e9edef]">Tu negocio</div>
              <div className="text-[11px] text-[#8696a0] mt-[1px]">en línea</div>
            </div>
          </div>

          {/* Body */}
          <div
            ref={bodyRef}
            className="flex-1 flex flex-col gap-2 overflow-y-auto"
            style={{
              padding: '14px 12px',
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)',
              backgroundSize: '14px 14px',
            }}
          >
            {shown.map((msg, i) => (
              <ChatBubble key={i} msg={msg} />
            ))}
            {typing && <TypingRow />}
          </div>

          {/* Input bar */}
          <div className="flex items-center gap-2 flex-shrink-0" style={{ background: '#1f2c34', padding: '8px 12px' }}>
            <div className="flex-1" style={{ background: '#2a3942', borderRadius: '20px', height: '30px' }} />
            <div className="w-[30px] h-[30px] rounded-full flex-shrink-0" style={{ background: '#25D366' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fireIn { to { opacity: 1; transform: translateY(0); } }
        @keyframes typingBlink {
          0%, 60%, 100% { opacity: .3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-2px); }
        }
      `}</style>
    </div>
  );
}

export default function Hero({ onCTAClick }: HeroProps) {
  return (
    <section className="relative overflow-hidden bg-[#080810]" style={{ paddingTop: '80px' }}>
      <div className="absolute inset-0 opacity-[0.15]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(123, 63, 228, 0.15) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(123, 63, 228, 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite',
          }}
        />
      </div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#7B3FE4]/8 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 py-10 sm:py-16 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 lg:gap-20 items-center">
        <div className="space-y-6 sm:space-y-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-[2px] bg-[#7B3FE4]" />
            <span
              className="text-[#8887A0] uppercase font-medium"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', letterSpacing: '0.2em' }}
            >
              INTEVOLABS, AUTOMATIZACIÓN CON IA
            </span>
          </div>

          <div>
            <h1
              className="font-bold text-[#F0EFF8] leading-[1.1]"
              style={{
                fontFamily: 'Sora, sans-serif',
                letterSpacing: '-0.02em',
                fontSize: 'clamp(36px, 6vw, 56px)',
                maxWidth: '620px',
              }}
            >
              Tu WhatsApp, respondiendo solo. Tu negocio,{' '}
              <span className="bg-gradient-to-r from-[#F0EFF8] to-[#7B3FE4] bg-clip-text text-transparent">
                operando solo.
              </span>
            </h1>
          </div>

          <p
            className="text-[#8887A0]"
            style={{ fontFamily: 'Sora, sans-serif', fontSize: '16px', lineHeight: '1.7', maxWidth: '560px' }}
          >
            Empezamos por WhatsApp, ahí es donde tus clientes ya te escriben. Seguimos conectando el resto: facturas, cotizaciones, reportes, los sistemas que ya usas. Todo con IA, sin que nadie tenga que hacerlo a mano.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-2">
            <button
              onClick={onCTAClick}
              className="w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-[#7B3FE4] text-white text-base sm:text-lg hover:bg-[#6B2FD4] transition-all duration-200 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer group"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, borderRadius: '50px', boxShadow: '0 0 32px rgba(123,63,228,0.7)' }}
            >
              Solicitar Diagnóstico Gratis
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-200"></i>
            </button>

            <a
              href={READY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 sm:py-5 text-[#F0EFF8] text-base border border-white/[0.14] hover:border-[#7B3FE4] hover:bg-[#7B3FE4]/[0.08] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, borderRadius: '50px' }}
            >
              Ver planes Ready
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>

          {/* WhatsApp mockup — mobile only, shown below CTAs */}
          <div className="lg:hidden pt-4">
            <WhatsAppPhone />
          </div>
        </div>

        {/* Right - WhatsApp mockup — desktop only */}
        <div className="relative hidden lg:flex items-center justify-center">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[500px] bg-[#7B3FE4]/10 rounded-full blur-[100px] pointer-events-none" />
          <WhatsAppPhone />
        </div>
      </div>

      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
      `}</style>
    </section>
  );
}
